import React from "react";

import {
  StyleSheet,
  View,
  ScrollView,
  KeyboardAvoidingView,
} from "react-native";

import Utils, {
  hasItems,
  isWeb,
  navigate,
  getElementModel,
  setStorageItem,
  getStorageKey,
  isAndroid,
} from "../../../../helpers/utils";

import { cloneDeep } from "lodash";
import moment from "moment";

import AppMessages from "../../../../config/AppMessages";
import appPages from "../../../../appPages";
import appStyles from "../../../appStyles";
import BaseCoreElementsPicker from "./BaseCoreElementsPicker";
import BottomButton from "../../common/BottomButton";
import CreateElementFabButton from "./CreateElementFabButton";
import EditElementFabButton from "./EditElementFabButton";
import EditElementsToggle from "./EditElementsToggle";
import ElementsHelper from "../ElementsHelper";
import ElementsList from "../list/ElementsList";
import ElementSmileyIcon from "../../common/icons/elements/ElementSmileyIcon";
import Header from "../../common/header";
import Icons from "../../common/icons";
import ImageComponent from "../../common/ImageComponent";
import ManaEmojiPicker from "../../common/emoji-picker/ManaEmojiPicker";
import ManaTextInput from "../../common/ManaTextInput";
import Search from "../../common/search-bar/Search";
import MenuEditOptionsPanel from "../../common/MenuEditOptionsPanel";

export default class EditElementPage extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    const { element, returnUrl } = this._getArgs() || {};

    if (!element) {
      return navigate(this.controller, appPages.elementsPage);
    }

    const showParents = element.edit_is_major != 1 && element.edit_is_core != 1;
    const childrenIds = new Set(element.children || []);
    const parentIds = new Set(element.parents || []);

    this.state = {
      isLoading: true,
      openEmojiDialog: false,
      elements: [],
      isDialogOpen: false,
      isKeyboardActive: false,
      isAutoTracked: true, //TODO: element.is_tracked,

      element,
      isEditMode: !!element.edit_id,
      parentIds,
      childrenIds,
      showParents,
      returnUrl,
    };

    setTimeout(() => {
      this._updateElementsList(element, showParents);
    }, 200);
  }

  _getArgs() {
    if (this.controller.hasNavigation()) {
      return this.props.route?.params?.args;
    } else {
      return this.controller.state.screenArgs?.args?.args;
    }
  }

  _updateElementsList = (selectedElement, showParents) => {
    const { allElements, parentIds, childrenIds, element } = this.state;

    let activeElements = hasItems(allElements)
      ? allElements
      : this.controller.state.elements.filter((a) => a.is_active == 1);

    const selectedIds = showParents ? [...parentIds] : [...childrenIds];

    let selectedElements = [];
    activeElements.forEach((el) => {
      // For sorting
      if (el.is_core == 1) el.is_major = 1;

      if (el.id != selectedElement.edit_id) {
        el.is_selected = selectedIds.includes(el.id) ? 1 : 0;
        selectedElements.push(el);
      }
    });

    const sortBy = showParents ? "checked|is_major" : "checked|name";
    const elements = ElementsHelper.sortElements(selectedElements, sortBy);

    this.setState((state) => ({
      ...state,
      isLoading: false,
      showParents,
      elements,
      allElements: cloneDeep(elements),
      mariMessage: this.getMariMessage(element, childrenIds, showParents),
    }));
  };

  updateName = (name) => {
    this.setState(
      (state) => ({
        ...state,
        element: {
          ...this.state.element,
          edit_name: name,
        },
      }),
      () => this.saveElement(),
    );
  };

  toggleFavoriteState = () => {
    const { element } = this.state;

    this.setState(
      (state) => ({
        ...state,
        element: {
          ...element,
          edit_is_favorite: !element.edit_is_favorite,
        },
      }),
      () => this.saveElement(),
    );
  };

  // ------------------------------------- SELECTED -------------------------------------
  toggleParentsView = () => {
    const { element, showParents } = this.state;
    this._updateElementsList(element, !showParents);
  };

  updateSelectedElements = (selectedElement, isSelected) => {
    const { element, parentIds, childrenIds, showParents } = this.state;

    let newIds = showParents ? parentIds : childrenIds;

    if (isSelected) {
      newIds.add(selectedElement.id);
    } else {
      newIds.delete(selectedElement.id);
    }

    this.setState(
      (state) => ({
        ...state,
        [showParents ? "parentIds" : "childrenIds"]: newIds,
        mariMessage: this.getMariMessage(element, newIds, showParents),
      }),
      () => this.saveElement(),
    );
  };

  getMariMessage = (element, children, showParents) => {
    if (showParents) return "";

    return element.edit_id &&
      // 2021.09.21 show error for all elements
      //(element.edit_is_major == 1 || element.edit_is_core == 1) &&
      children.size == 0
      ? AppMessages.NoChildrenSelected
      : "";
  };

  // Base Core Element selected from dialog
  onCoreElementSelected = (coreElementId) => {
    const { parentIds } = this.state;

    if (coreElementId) {
      this.setState(
        (state) => ({
          ...state,
          isDialogOpen: false,
          parentIds: [...parentIds, coreElementId],
        }),
        () => this.onSaveValidated(),
      );
      // User confirmed no parent
    } else {
      this.onSaveValidated();
    }
  };

  // Create new core element from dialog
  createCoreElement = (name) => {
    const model = getElementModel({ name, is_core: 1 });

    this.controller.cardActionWithCallback(
      {
        action: "save",
        target: "element",
        ...model,
      },
      (data, response) => {
        if (response.id) this.onCoreElementSelected(response.id);
      },
      (error) => {
        if (error?.error) {
          this.setState((state) => ({
            ...state,
            mariMessage: {
              message: error.error,
              hasError: true,
            },
          }));
        }
      },
    );
  };

  // ------------------------------------- SAVE -------------------------------------
  showErrorMessage = () => {
    this.setState((state) => ({
      ...state,
      isInvalid: true,
      isDialogOpen: false,
      mariMessage: {
        message: AppMessages.ParentElementIsRequired,
        hasError: true,
      },
    }));

    setTimeout(() => {
      this.setState((state) => ({
        ...state,
        mariMessage: "",
      }));
    }, 3000);
  };

  isValidModel = () => {
    const { element } = this.state;
    return element.edit_name;
  };

  // Activity is saved along with copied tags stored temp
  saveElement = (userTriggered = false) => {
    const { element, parentIds, isEditMode } = this.state;

    // On create page save is triggered by the user
    if (!isEditMode && !userTriggered) return;

    if (!this.isValidModel()) {
      this.showErrorMessage();
      return;
    }

    if (!isEditMode && element.edit_is_core != 1 && parentIds.size == 0) {
      // Show core elements dialog picker
      this.setState((state) => ({ ...state, isDialogOpen: true }));
      return;
    }

    this.onSaveValidated();
  };

  onSaveValidated = () => {
    const { element, parentIds, childrenIds } = this.state;

    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));

    const elementModel = {
      ...element,
      edit_is_active: "checked",
      parents: [...parentIds],
      children: [...childrenIds],
    };

    this.setState((state) => ({
      ...state,
      isLoading: false,
    }));

    this.controller.tracking.track("Edit Element");
    this.controller.cardActionWithCallback(
      {
        action: "save",
        target: "element",
        ...elementModel,
        requestPage: "rn:elements",
        requestArgs: {},
      },
      (data, response) => this.onSaveComplete(response),
      (error) => {
        if (error?.error) {
          this.setState((state) => ({
            ...state,
            mariMessage: {
              message: error.error,
              hasError: true,
            },
          }));
        }
      },
    );
  };

  onSaveComplete = (response) => {
    const { element, isEditMode } = this.state;

    this.setState(
      (state) => ({
        ...state,
        isLoading: false,
        isInvalid: false,
        //mariMessage: "",
        element: {
          ...element,
          id: response?.id,
          item_type: response?.element,
        },
      }),
      () => {
        if (!isEditMode) this.goBack();
      },
    );
  };

  goBack = () => {
    const { element, isInvalid } = this.state;
    const { timeslot, integration } = element;

    // New element was created coming from mapping game
    if (integration) {
      return this.controller.navigate(appPages.integrationsMappingGame, {
        element,
      });
    }

    // Activity might be created from empty timeslot tracker
    if (timeslot != null && timeslot.timeslot_id && element.id) {
      // Refresh + redirect
      return this.controller.getApiData(
        "timeslots",
        { timeslotsDate: moment(timeslot.end_time).format("YYYY-MM-DD") },
        () => {
          this.controller.navigate(appPages.trackHome);
        },
      );
    }

    // From routines
    if (this.state.returnUrl == "routines") {
      setStorageItem(
        getStorageKey(this.controller, "routine_element"),
        element.id,
      );
    }

    if (this.controller.hasNavigation() && this.controller.navigation) {
      this.controller.goBack();
      return;
    }

    // Do not allow the user to leave if no core element nor a base tag was selected
    if (isInvalid) {
      this.showErrorMessage();
      return;
    }

    const returnUrl = this.state.returnUrl || this.controller.getReturnUrl();
    const returnArgs =
      returnUrl == "onboarding_template"
        ? {
            args: {
              element: {
                id_activity: element.id,
                item_type: element.element,
              },
            },
          }
        : {};

    let returnPage = returnUrl || appPages.elementsPage;
    setTimeout(() => {
      this.controller.getItems("feed", returnPage, returnArgs);
    }, 100);
  };
  // ------------------------------------- FILTERING -------------------------------------
  filterByKeyword = ({ term }) => {
    this.filterResults({ ...this.state.filters, term });
  };

  filterResults = ({ term }) => {
    const { showParents, parentIds, childrenIds, allElements } = this.state;

    let filteredElements = cloneDeep(allElements) || [];

    // Multi terms search
    if (term) {
      const pattern = Utils.getSearchPattern(term);
      filteredElements = filteredElements.filter((a) => {
        return a.name && a.name.match(pattern);
      });
    }

    const ids = showParents ? [...parentIds] : [...childrenIds];
    filteredElements.forEach((el) => {
      el.is_selected = ids.includes(el.id);
    });

    this.setState((state) => ({
      ...state,
      elements: [...filteredElements],
    }));
  };

  // ------------------------------------- EMOJI -------------------------------------
  toggleEmojiDialog = (isOpen = true) => {
    this.setState((state) => ({ ...state, openEmojiDialog: isOpen }));
  };

  updateEmoji = (emoji) => {
    this.setState(
      (state) => ({
        ...state,
        element: {
          ...this.state.element,
          edit_icon: emoji.unified || emoji.id,
        },
      }),
      () => this.saveElement(),
    );
  };

  // ------------------------------------- FAB -------------------------------------
  toggleAutoTrack = () => {
    this.setState((state) => ({
      ...state,
      isAutoTracked: !state.isAutoTracked,
    }));
  };

  goToReflection = () => {
    const { element } = this.state;

    navigate(this.controller, "rn:sets_reflection_detail", "changePage", {
      reflection: {
        edit_time_unit: "week",
        edit_element_id: element.edit_id,
        edit_icon: element.edit_icon,
        edit_name: element.edit_name,
      },
      fetchFromApi: true,
    });
  };

  goToEditElementPage = (element) => {
    const elementData = ElementsHelper.findById(this.controller, element.id);
    if (elementData) {
      if (this.props.navigation) {
        this.props.navigation.push("edit_element", {
          selectedPage: "rn:edit_element",
          args: {
            element: getElementModel(elementData),
          },
        });
      } else {
        navigate(this.controller, "rn:edit_element", "changepage", {
          element: getElementModel(elementData),
        });
      }
    }
  };

  toggleEditMenuOptions = (el) => {
    this.setState((state) => ({
      ...state,
      selectedElement: el,
    }));
  };

  getMenuOptions = () => {
    let options = [
      {
        text: "Edit Element",
        action: () => this.onMenuAction("edit_element"),
      },
    ];
    return options;
  };

  onMenuAction = (actionType = "edit") => {
    const { selectedElement } = this.state;
    if (!selectedElement) return;

    this.setState((state) => ({ ...state, selectedElement: null }));

    switch (actionType) {
      case "edit_element":
        this.goToEditElementPage(selectedElement);
        break;
      default:
        break;
    }
  };

  render() {
    const {
      isLoading,
      openEmojiDialog,
      isEditMode,
      showParents,
      isAutoTracked,
      element,
      elements,
      mariMessage,
      isDialogOpen,
      selectedElement,
    } = this.state;

    const isNew = element.edit_id == null || element.edit_id == "";

    const elementLabel = element.edit_is_core == 1 ? "Core Element" : "Element";
    const headerText = (isNew ? "New " : "Edit ") + elementLabel;
    const placeHolderText = `Enter ${elementLabel} Name`;
    const buttonText = `Create ${elementLabel}`;

    const subTitle =
      element.edit_is_core == 1 ? "Choose Elements to Include" : "";

    return (
      <View style={appStyles.container}>
        <ManaEmojiPicker
          isOpen={openEmojiDialog}
          onConfirm={this.updateEmoji}
          onClose={() => this.toggleEmojiDialog(false)}
        />

        {/* Creating a new Core Element without parents */}
        <BaseCoreElementsPicker
          controller={this.controller}
          isOpen={isDialogOpen}
          onCoreElementSelected={this.onCoreElementSelected}
          createCoreElement={this.createCoreElement}
          onClose={() => {
            this.setState((state) => ({ ...state, isDialogOpen: false }));
          }}
        />

        <KeyboardAvoidingView
          behavior="height"
          style={isEditMode ? appStyles.kbViewNoFooter : appStyles.kbView}
          keyboardVerticalOffset={isEditMode ? 0 : isAndroid ? 60 : 0}
        >
          <Header
            controller={this.controller}
            title={headerText}
            subTitle={subTitle}
            hideAvatar
            goBack={this.goBack}
            isLoading={isLoading}
          />

          <View style={appStyles.preList} />

          <ScrollView
            contentContainerStyle={appStyles.scrollViewContent}
            style={[
              appStyles.scrollView,
              {
                backgroundColor: "#FFF",
              },
            ]}
          >
            {/* Name & icon */}
            <View style={styles.inputContainer}>
              <View style={[styles.imageContainer]}>
                {element.edit_icon ? (
                  <ImageComponent
                    style={styles.icon}
                    icon={element && element.edit_icon}
                    onPress={() => this.toggleEmojiDialog(true)}
                  />
                ) : (
                  <ElementSmileyIcon
                    style={styles.icon}
                    onPress={() => this.toggleEmojiDialog(true)}
                  />
                )}
              </View>

              <ManaTextInput
                style={styles.input}
                placeholderTextColor="#ABADB3"
                contentHeight={40}
                name={"activityEdit"}
                placeholder={placeHolderText}
                autoFocus={isNew}
                onTextChanged={(text) => this.updateName(text)}
                value={element.edit_name || ""}
              />

              <View style={styles.favoriteIcon}>
                <Icons.Favorite
                  isFavorite={element.edit_is_favorite == 1}
                  toggleFavoriteState={this.toggleFavoriteState}
                />
              </View>
            </View>

            {/* Parents/Children toggle */}
            <EditElementsToggle
              showParents={showParents}
              toggleParentsView={this.toggleParentsView}
            />

            {/* Elements list */}
            {!isLoading && hasItems(elements) ? (
              <ElementsList
                elements={elements}
                controller={this.controller}
                onElementSelected={this.updateSelectedElements}
                showEditOptions={this.toggleEditMenuOptions}
                showSelector
              />
            ) : null}
          </ScrollView>

          {selectedElement ? (
            <MenuEditOptionsPanel
              isPanelActive={!!selectedElement}
              close={() => this.toggleEditMenuOptions(null)}
              options={this.getMenuOptions()}
            />
          ) : null}

          <View
            style={[appStyles.footerOptions, isEditMode ? {} : { bottom: 16 }]}
          >
            <Search
              placeholder="Search Elements or Create New"
              filterResults={this.filterByKeyword}
              controller={this.controller}
              autoFocus={!isNew && isWeb}
            />
          </View>
        </KeyboardAvoidingView>

        <View style={[appStyles.footerOptions]}>
          {isEditMode ? (
            <EditElementFabButton
              toggleAutoTrack={this.toggleAutoTrack}
              isAutoTracked={isAutoTracked}
              goToReflection={this.goToReflection}
              mariOptions={{ mariMessage }}
            />
          ) : (
            <>
              <CreateElementFabButton
                mariOptions={{ mariMessage }}
                style={{
                  bottom: isWeb ? 100 : 106,
                }}
              />

              <BottomButton
                disabled={!this.isValidModel()}
                style={{ height: 40, width: 220 }}
                buttonText={buttonText}
                onPress={() => this.saveElement(true)}
              />
            </>
          )}
        </View>
      </View>
    );
  }
}

const styles = StyleSheet.create({
  inputContainer: {
    position: "relative",
    height: 40,
    marginBottom: 12,
    marginHorizontal: 6,
  },
  input: {
    paddingLeft: 50,
    margin: 0,
    maxHeight: 40,
    marginBottom: 0,
  },
  favoriteIcon: {
    position: "absolute",
    right: 0,
    top: 0,
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  imageContainer: {
    position: "absolute",
    left: 0,
    top: 0,
    width: 40,
    height: 40,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 100,
    elevation: 3,
  },
  icon: {
    width: 30,
    height: 30,
  },
});
